import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Appointment, AppointmentTypes } from "../models/entities/appointments.model";
import { EndpointsService } from "../services/endpoints/endpoints.service";
import { toLocalDateParam } from "../pages/home/rail/appointments/appointments-rail.service";
import { useAppointmentsTypes } from "./useAppointmentsTypes";

export const TODAYS_APPOINTMENTS_QUERY_KEY = "home-todays-appointments";

/**
 * Appointments change rarely inside a day; the rail expires cards on a
 * 1-minute tick in the component instead of polling the API.
 */
export const TODAYS_APPOINTMENTS_REFETCH_INTERVAL_MS = 15 * 60 * 1000;

export interface UseTodaysAppointmentsResult {
	appointments: Appointment[];
	types: AppointmentTypes[];
	isLoading: boolean;
	isError: boolean;
	error: unknown;
}

/**
 * The logged-in coach's appointments for today, in the coach's local timezone.
 *
 * Appointments are queried once per page entry and refreshed on a 15-minute
 * interval (appointments change rarely during a day). Visibility inside the
 * rail is managed by a 1-minute tick in the component, not by API polling.
 *
 * The coach ID is sent unconditionally, including in admin view — do NOT copy
 * the `!HelperService.isAdminViewMode()` guard from ParticipantsAppointments.
 * Maria: "yours only, even on admin view".
 *
 * A failed `/appointment-types` lookup must NOT blank the rail; it degrades
 * to unknown durations (a 30-minute grace period per card).
 */
export const useTodaysAppointments = (
	coachId: number | undefined | null
): UseTodaysAppointmentsResult => {
	const dateParam = useMemo(() => toLocalDateParam(new Date()), []);

	const appointmentsQuery = useQuery({
		queryKey: [TODAYS_APPOINTMENTS_QUERY_KEY, coachId, dateParam],
		queryFn: () =>
			EndpointsService.appointments.getAppointments({
				config: {
					params: {
						minDate: dateParam,
						maxDate: dateParam,
						gladstoneCoachId: coachId != null ? `${coachId}` : null,
					},
				},
			}),
		enabled: !!coachId,
		refetchOnWindowFocus: false,
		refetchInterval: TODAYS_APPOINTMENTS_REFETCH_INTERVAL_MS,
	});

	const typesQuery = useAppointmentsTypes();

	return {
		appointments: appointmentsQuery.data ?? [],
		types: typesQuery.data ?? [],
		isLoading: appointmentsQuery.isLoading,
		isError: !!appointmentsQuery.error,
		error: appointmentsQuery.error,
	};
};
